/* global CashflowModuleIndex CashflowModuleFiltersUrl ExpenseMilestoneUpdateUrl SponsorMilestoneUpdateUrl RevenueMilestoneUpdateUrl exportCashflowUrl importCashflowUrl minDate maxDate canEdit */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import axios from 'axios';
import moment from 'moment';

import DepartmentCashflowModuleTable from './DepartmentCashflowModuleTable';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableFilterModal from 'CommonComponents/TableFilterModal';

import EditIcon from 'Assets/icons/edit.svg';
import ExportIcon from 'Assets/icons/export.svg';
import RefreshSync from 'Assets/icons/refresh-sync.svg';

function DepartmentCashflowModule(props) {
    const [refreshCashflowCount, setRefreshCashflow] = useState(0);
    const [selectedMonths, setSelectedMonths] = useState({});
    const [tempSelectedMonths, setTempSelectedMonths] = useState({});
    const [availableMonths, setAvailableMonths] = useState([]);
    const [displayBudgetLines, setDisplayBudgetLines] = useState({ committed: true, uncommitted: true });
    const [tempDisplayBudgetLines, setTempDisplayBudgetLines] = useState({ committed: true, uncommitted: true });
    const [isLoadingCachedFilters, setIsLoadingCachedFilters] = useState(false);
    const [editAllTrigger, setEditAllTrigger] = useState({ on: false, target: 'all', version: 0 });
    const [isEditAllProcessing, setIsEditAllProcessing] = useState(false);
    const [expandAllTrigger, setExpandAllTrigger] = useState({ expanded: false, timestamp: null, batch: null, version: 0, isLast: false });
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentOperation, setCurrentOperation] = useState(null);
    const [currentStage, setCurrentStage] = useState(null);

    const downloadCashflow = () => {
        window.open(exportCashflowUrl, '_blank');
    };

    const refreshCashflow = () => {
        setRefreshCashflow(refreshCashflowCount + 1);
    };

    const handleEditAll = () => {
        setIsEditAllProcessing(true);
        setEditAllTrigger((prev) => ({ on: true, target: 'all', version: prev.version + 1 }));

        // Simulate processing time and then reset
        setTimeout(() => {
            setIsEditAllProcessing(false);
        }, 1000);
    };

    const handleCancelEditAll = () => {
        setIsEditAllProcessing(true);
        setEditAllTrigger((prev) => ({ on: false, target: 'all', version: prev.version + 1 }));

        // Simulate processing time and then reset
        setTimeout(() => {
            setIsEditAllProcessing(false);
        }, 1000);
    };

    const timeoutRef = useRef(null);
    const batchTimeoutRef = useRef(null);
    const processingRef = useRef(false);

    const toggleAllExpansion = useCallback((expandedVal = true) => {
        // Prevent multiple simultaneous operations
        if (processingRef.current) return;

        processingRef.current = true;
        setIsProcessing(true);
        setCurrentOperation(expandedVal ? 'expand' : 'collapse');

        // Clear any pending timeouts
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (batchTimeoutRef.current) {
            clearTimeout(batchTimeoutRef.current);
        }

        // Create batched updates with staggered timing
        const batches = [
            { type: 'sections', delay: 0 },
            { type: 'departments', delay: 50 },
            { type: 'costcenters', delay: 100 },
            { type: 'rows', delay: 150 },
            { type: 'uncommitted', delay: 200 },
            { type: 'committed', delay: 250 },
            { type: 'projects', delay: 300 },
            { type: 'projects uncommitted', delay: 350 },
            { type: 'projects committed', delay: 400 },
        ];

        // Start the batched update process
        batches.forEach((batch, index) => {
            batchTimeoutRef.current = setTimeout(() => {
                setCurrentStage(batch.type);
                setExpandAllTrigger((prev) => ({
                    expanded: expandedVal,
                    timestamp: Date.now(),
                    batch: batch.type,
                    version: prev.version + 1, // strictly increases every trigger
                    isLast: index === batches.length - 1,
                }));

                // Reset processing state after the last batch
                if (index === batches.length - 1) {
                    timeoutRef.current = setTimeout(() => {
                        processingRef.current = false;
                        setIsProcessing(false);
                        setCurrentOperation(null);
                        setCurrentStage(null);
                    }, 200);
                }
            }, batch.delay);
        });
    }, []);

    // Generate months between minDate and maxDate using moment.js
    const generateMonthsList = useCallback(() => {
        if (!minDate || !maxDate) return [];

        const months = [];
        const startMoment = moment(minDate).startOf('month');
        const endMoment = moment(maxDate).startOf('month');
        const currentMoment = startMoment.clone();

        while (currentMoment.isSameOrBefore(endMoment)) {
            const monthKey = currentMoment.format('YYYY-MM');
            const monthLabel = currentMoment.format('MMMM YYYY');

            months.push({
                key: monthKey,
                label: monthLabel,
                value: monthKey,
            });

            // Move to next month
            currentMoment.add(1, 'month');
        }

        return months;
    }, [minDate, maxDate]);

    // Initialize everything in a single effect
    useEffect(() => {
        const initializeEverything = async () => {
            const months = generateMonthsList();
            setAvailableMonths(months);

            setIsLoadingCachedFilters(true);
            try {
                const response = await axios.get(CashflowModuleFiltersUrl);
                const { selectedMonths: cachedSelectedMonths, displayBudgetLines: cachedDisplayBudgetLines } = response.data;

                // Use cached filters if available, otherwise use defaults
                const finalSelectedMonths =
                    cachedSelectedMonths && Object.keys(cachedSelectedMonths).length > 0
                        ? cachedSelectedMonths
                        : months.reduce((acc, month) => ({ ...acc, [month.key]: true }), {});
                const finalDisplayBudgetLines = cachedDisplayBudgetLines || { committed: true, uncommitted: true };

                // Set all states at once
                setSelectedMonths(finalSelectedMonths);
                setTempSelectedMonths(finalSelectedMonths);
                setDisplayBudgetLines(finalDisplayBudgetLines);
                setTempDisplayBudgetLines(finalDisplayBudgetLines);
            } catch (error) {
                console.log('Error fetching cached filters:', error);
                // Use default values
                const initialSelection = months.reduce((acc, month) => ({ ...acc, [month.key]: true }), {});
                setSelectedMonths(initialSelection);
                setTempSelectedMonths(initialSelection);
                setDisplayBudgetLines({ committed: true, uncommitted: true });
                setTempDisplayBudgetLines({ committed: true, uncommitted: true });
            } finally {
                setIsLoadingCachedFilters(false);

                // Trigger refresh to fetch data
                setRefreshCashflow(refreshCashflowCount + 1);
            }
        };

        initializeEverything();
    }, [generateMonthsList]);

    // Month filter functions
    const toggleMonth = (monthKey) => {
        setTempSelectedMonths((prev) => ({
            ...prev,
            [monthKey]: !prev[monthKey],
        }));
    };

    // Display budget lines filter functions
    const toggleDisplayBudgetLine = (type) => {
        setTempDisplayBudgetLines((prev) => ({
            ...prev,
            [type]: !prev[type],
        }));
    };

    const onApply = () => {
        // Apply the temporary selections to the actual selectedMonths state
        setSelectedMonths(tempSelectedMonths);
        // Apply the temporary display budget lines selections
        setDisplayBudgetLines(tempDisplayBudgetLines);
        // Trigger refresh to fetch data with new month selection
        setRefreshCashflow(refreshCashflowCount + 1);
    };

    const onModalOpen = () => {
        // Reset temporary state to current selected months when modal opens
        setTempSelectedMonths(selectedMonths);
        // Reset temporary display budget lines state
        setTempDisplayBudgetLines(displayBudgetLines);
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            <div className="tw-flex tw-flex-row tw-justify-between tw-gap-x-2">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-flex tw-gap-x-1.5">
                        <button
                            className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline"
                            onClick={() => toggleAllExpansion()}
                            disabled={isProcessing}
                            style={{ opacity: isProcessing ? 0.6 : 1 }}
                        >
                            {isProcessing ? $t('Processing') : $t('Expand all')}
                        </button>
                        <button
                            className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline"
                            onClick={() => toggleAllExpansion(false)}
                            disabled={isProcessing}
                            style={{ opacity: isProcessing ? 0.6 : 1 }}
                        >
                            {isProcessing ? $t('Processing') : $t('Collapse all')}
                        </button>
                    </div>
                </div>
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-mr-2">
                        <TableFilterModal
                            id="department-cashflow-module_month_filter-modal"
                            showCancel={true}
                            onApply={onApply}
                            onOpen={onModalOpen}
                            cancelText={$t('Cancel')}
                            applyText={$t('Apply')}
                            headerText={$t('Filter by Month')}
                        >
                            <div className="tw-space-y-4">
                                <div>
                                    <div className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">{$t('Months')}</div>
                                    <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                                        {availableMonths.map((month) => {
                                            const checked = !!tempSelectedMonths[month.key];
                                            return (
                                                <div key={month.key}>
                                                    <CheckboxInput label={month.label} checked={checked} onChange={() => toggleMonth(month.key)} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div>
                                    <div className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">{$t('Display Budget Lines')}</div>
                                    <div className="tw-space-y-2">
                                        <div>
                                            <CheckboxInput
                                                label={$t('Committed')}
                                                checked={tempDisplayBudgetLines.committed}
                                                onChange={() => toggleDisplayBudgetLine('committed')}
                                            />
                                        </div>
                                        <div>
                                            <CheckboxInput
                                                label={$t('Uncommitted')}
                                                checked={tempDisplayBudgetLines.uncommitted}
                                                onChange={() => toggleDisplayBudgetLine('uncommitted')}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TableFilterModal>
                    </div>
                    <Button
                        variant={editAllTrigger.on ? 'secondary' : 'primary'}
                        onClick={editAllTrigger.on ? handleCancelEditAll : handleEditAll}
                        disabled={isEditAllProcessing}
                        style={{ opacity: isEditAllProcessing ? 0.6 : 1 }}
                    >
                        {isEditAllProcessing ? (
                            <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-b-2 tw-border-white"></div>
                        ) : (
                            <EditIcon className="tw-w-3 tw-h-3" />
                        )}
                        {isEditAllProcessing ? $t('Processing...') : editAllTrigger.on ? $t('Cancel Edit All') : $t('Edit All')}
                    </Button>
                    <Button variant="primary" onClick={() => refreshCashflow()}>
                        <RefreshSync className="tw-w-3 tw-h-3" />
                        {$t('Refresh Totals')}
                    </Button>
                    <Button variant="primary" as="a" href={importCashflowUrl} data-action="navigate">
                        <ExportIcon className="tw-w-3 tw-h-3" />
                        {$t('Import from file')}
                    </Button>
                    <Button variant="primary" onClick={() => downloadCashflow()}>
                        {$t('Download')}
                    </Button>
                </div>
            </div>
            <div className="tw-relative">
                {(isProcessing || isEditAllProcessing) && (
                    <div className="tw-absolute tw-inset-0 tw-bg-white tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                        <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-4 tw-flex tw-items-center tw-gap-x-3">
                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-b-2 tw-border-blue-600"></div>
                            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                                {(() => {
                                    if (isEditAllProcessing) {
                                        return editAllTrigger.on ? $t('Enabling edit mode for all rows...') : $t('Disabling edit mode for all rows...');
                                    }
                                    if (currentOperation === 'expand') {
                                        return $t('Expanding {{stage}}', { stage: currentStage });
                                    }
                                    return $t('Collapsing {{stage}}...', { stage: currentStage });
                                })()}
                            </span>
                        </div>
                    </div>
                )}
                <DepartmentCashflowModuleTable
                    CashflowModuleIndex={CashflowModuleIndex}
                    refreshCashflowCount={refreshCashflowCount}
                    ExpenseMilestoneUpdateUrl={ExpenseMilestoneUpdateUrl}
                    SponsorMilestoneUpdateUrl={SponsorMilestoneUpdateUrl}
                    RevenueMilestoneUpdateUrl={RevenueMilestoneUpdateUrl}
                    minDate={minDate}
                    maxDate={maxDate}
                    canEdit={canEdit}
                    selectedMonths={selectedMonths}
                    displayBudgetLines={displayBudgetLines}
                    isLoadingCachedFilters={isLoadingCachedFilters}
                    editAllTrigger={editAllTrigger}
                    expandAllTrigger={expandAllTrigger}
                />
            </div>
        </div>
    );
}

if (document.getElementById('departmentCashflowModule')) {
    const rootEl = document.getElementById('departmentCashflowModule');
    ReactDOM.render(
        <App>
            <DepartmentCashflowModule />
        </App>,
        rootEl,
    );
}
